import qs from 'qs';
import { axiosClient } from '@/libs/axios-client';
import { INCOMING_INVOICES_FIELD_MAP } from '@/shared/incoming-invoices';
import { OUTGOING_INVOICES_FIELD_MAP } from '@/shared/outgoing-invoices';
import {
  customUrlEncode,
  prepareFilters,
  preparePagination,
  prepareSorters,
} from '@/utils/apiParamsUtils';
import { escapeRegExp } from '@/utils/filterUtils';

/**
 * Normalizes UI filters into API shape using a field map.
 * - Moves reference-backed fields into `filters.ref`.
 * - For text ref fields, wraps value into case-insensitive `$regex`.
 * - For non-text ref fields (date/number/range), passes through as-is
 *   (backend will coerce ranges on ref string dates).
 *
 * @param {Object} filters
 * @param {Object} FIELD_MAP - field map (outgoing/incoming)
 * @returns {Object}
 */
export function normalizeInvoiceFilters(
  filters,
  FIELD_MAP = OUTGOING_INVOICES_FIELD_MAP
) {
  const top = { ...filters };
  const ref = {};

  for (const key of Object.keys(FIELD_MAP)) {
    const meta = FIELD_MAP[key];
    if (!meta) continue;

    const val = top[key];
    if (val == null || val === '') continue;

    if (meta.isRef) {
      const k = meta.filterKey || key;

      if (meta.type === 'text') {
        // contains, case-insensitive
        ref[k] = { $regex: escapeRegExp(String(val)), $options: 'i' };
      } else {
        // pass-through (range/date/number/object) — backend will handle
        ref[k] = val;
      }

      delete top[key];
    }
  }

  if (Object.keys(ref).length) {
    top.ref = { ...(top.ref || {}), ...ref };
  }

  return top;
}

/**
 * The shape `prepareFilters` expects, derived from the column's own type.
 *
 * 🚨 This table exists so the two never have to be kept in step by hand. A
 * column used to have to be named in five separate places before it worked —
 * the field map, this allow-list, the sorter map, and both export
 * configurations — and missing any one of them fails silently in its own way:
 * a filter left out here lights its funnel, puts its value in the URL, and
 * returns the whole table; a sorter left out moves its arrows and changes
 * nothing. Both were measured, more than once. Two of those five lists are now
 * read off the field map instead of written out again.
 */
const FILTER_SHAPE_BY_TYPE = Object.freeze({
  text: 'default',
  number: 'default',
  default: 'default',
  array: 'array',
  date: 'range',
});

/**
 * Filterable query params that are not columns on any screen — the company and
 * type the request is scoped by, the archive flag, and the product search that
 * reaches into a subdocument rather than a column.
 */
const NON_COLUMN_FILTERS = Object.freeze({
  company: 'default',
  type: 'default',
  archived: 'default',
  createdAt: 'range',
  productsService: 'array',
  productText: 'default',
});

/** Sortable params with no column of their own. */
const NON_COLUMN_SORTERS = Object.freeze({ archivedDate: 'sortArchivedDate' });

/**
 * The allow-list `prepareFilters` is handed, read off the field map.
 *
 * A column marked `sortOnly` is left out deliberately: declaring something
 * filterable that nothing can filter is the fault this whole derivation exists
 * to prevent.
 *
 * @param {Object} FIELD_MAP
 * @returns {Object} `{ paramName: 'default' | 'array' | 'range' }`
 */
export const filterAllowListOf = (FIELD_MAP = {}) =>
  Object.entries(FIELD_MAP).reduce((acc, [key, meta]) => {
    if (!meta || meta.sortOnly) return acc;

    acc[key] = FILTER_SHAPE_BY_TYPE[meta.type] || 'default';

    return acc;
  }, { ...NON_COLUMN_FILTERS });

/**
 * The map `prepareSorters` is handed, read off the field map.
 *
 * The param name is the column name with `sort` in front, which is the
 * convention every one of the hand-written entries already followed.
 *
 * @param {Object} FIELD_MAP
 * @returns {Object} `{ columnName: 'sortColumnName' }`
 */
export const sorterMapOf = (FIELD_MAP = {}) =>
  Object.entries(FIELD_MAP).reduce((acc, [key, meta]) => {
    if (!meta?.sortKey) return acc;

    acc[key] = `sort${key.charAt(0).toUpperCase()}${key.slice(1)}`;

    return acc;
  }, { ...NON_COLUMN_SORTERS });

/**
 * Remaps sorter keys to actual data paths via field map.
 * Unknown keys are passed through as-is.
 *
 * @param {Object} sorter
 * @param {Object} FIELD_MAP - field map (outgoing/incoming)
 * @returns {Object}
 */
export function normalizeInvoiceSorters(
  sorter = {},
  FIELD_MAP = OUTGOING_INVOICES_FIELD_MAP
) {
  if (!sorter || typeof sorter !== 'object') return sorter;

  const out = {};
  for (const [key, dir] of Object.entries(sorter)) {
    const meta = FIELD_MAP[key];
    const targetKey = meta?.sortKey || key;
    out[targetKey] = dir;
  }
  return out;
}

/**
 * Retrieves a list of invoices from the server with optional filters and pagination.
 *
 * @async
 * @function retrieveAllInvoices
 * @param {Object} params - Query parameters for filtering, sorting, and paginating invoices.
 * @param {Object} [params.filter] - Optional filters such as company and type.
 * @param {Object} [params.pagination] - Optional pagination settings (e.g., page, pageSize).
 * @returns {Promise<Object>} The response data containing the list of invoices and pagination info.
 * @throws Will throw an error if the request to retrieve invoices fails.
 */
/**
 * Turns raw query params into the filter and sorter the API expects.
 *
 * 🚨 Exported and shared on purpose. The invoice list and the journal totals
 * describe the same set of rows, and the totals go on a document an accounting
 * agency signs — so they must not build their filters separately. They did,
 * briefly, and the totals answered "0 documents" over a table showing 1.524:
 * the month arrives from the URL as a two-element array, `prepareFilters` turns
 * that into `{ from, to }`, and anything that skips this step hands the server
 * a shape it reads as a single day.
 *
 * @param {Object} params - Query params from the screen.
 * @returns {{ FIELD_MAP: Object, normalizedFilters: Object, normalizedSorters: Object }}
 */
export const buildInvoiceQuery = (params) => {
  // Decide which field map to use (incoming = purchase; outgoing = sales/regular)
  const types = Array.isArray(params?.type)
    ? params.type
    : [params?.type].filter(Boolean);
  const isIncoming =
    types.includes('ePurchaseInvoices') && !types.includes('eSalesInvoices');
  const FIELD_MAP = isIncoming
    ? INCOMING_INVOICES_FIELD_MAP
    : OUTGOING_INVOICES_FIELD_MAP;

  // 1) Build raw filters/sorters from query params.
  //
  // 🚨 Both lists are read off the field map rather than written out again —
  // see `filterAllowListOf`. Adding a column to the map is now the whole job:
  // it filters, it sorts, and neither can be forgotten separately. Measured
  // faults this removes: the project filter went out as `{company, type,
  // archived}` for a whole phase because nothing whitelisted it, and
  // `sortApprovedAt` moved its arrows over an unchanged table for the same
  // reason on the other list.
  const rawFilters = prepareFilters(params, filterAllowListOf(FIELD_MAP));

  const rawSorters = prepareSorters(params, sorterMapOf(FIELD_MAP));

  // 2) Pre-normalization tweaks
  // total -> backend totalAmount contains (string match on number-as-text)
  if (rawFilters.total != null && rawFilters.total !== '') {
    rawFilters.totalAmountContains = String(rawFilters.total);
    delete rawFilters.total;
  }

  const normalizedFilters = normalizeInvoiceFilters(rawFilters, FIELD_MAP);

  if (params?.ref && typeof params.ref === 'object') {
    normalizedFilters.ref = {
      ...(normalizedFilters.ref || {}),
      ...params.ref,
    };
  }

  // "Only invoices with a message waiting for me" — not a column, so no field
  // map carries it.
  if (String(params?.unreadMessages) === '1') {
    normalizedFilters.unreadMessages = '1';
  }

  const normalizedSorters = normalizeInvoiceSorters(rawSorters, FIELD_MAP);

  return { FIELD_MAP, normalizedFilters, normalizedSorters };
};

/**
 * Retrieves invoices for the list screens.
 *
 * @param {Object} params - Query parameters for filtering, sorting, paginating.
 * @returns {Promise<Object>} Items and count.
 * @throws Will throw an error if the request fails.
 */
export const retrieveAllInvoices = async (params) => {
  try {
    const { normalizedFilters, normalizedSorters } = buildInvoiceQuery(params);

    const pagination = preparePagination(params);

    const response = await axiosClient.get('/invoices', {
      params: {
        filter: normalizedFilters,
        sorter: normalizedSorters,
        ...pagination,
      },
      paramsSerializer: (p) => {
        try {
          if (p.filter) p.filter = JSON.stringify(p.filter);
          if (p.sorter) p.sorter = JSON.stringify(p.sorter);
        } catch (e) {
          console.warn(
            'retrieveAllInvoices: stringify failed, falling back.',
            e
          );
          p.filter = '{}';
          p.sorter = '{}';
        }
        return qs.stringify(p, {
          encode: true,
          arrayFormat: 'brackets',
          encoder: customUrlEncode,
        });
      },
    });

    return response.data;
  } catch (error) {
    if (error?.response) {
      console.error('retrieveAllInvoices: API error', {
        status: error.response.status,
        data: error.response.data,
      });
    } else {
      console.error('retrieveAllInvoices: client error', error);
    }
    throw error;
  }
};

/**
 * Updates the status of an invoice by its ID.
 *
 * @async
 * @function updateInvoiceStatusById
 * @param {string|number} invoiceId - The unique identifier of the invoice to update.
 * @param {Object} payload - The payload containing the new status data for the invoice.
 * @returns {Promise<Object>} The updated invoice data returned from the server.
 * @throws Will throw an error if the request fails.
 */
export const updateInvoiceStatusById = async (invoiceId, payload) => {
  try {
    const response = await axiosClient.patch(
      `/invoices/${invoiceId}/status`,
      payload
    );

    return response.data;
  } catch (error) {
    console.error(
      `Failed to update invoice status with ID ${invoiceId}:`,
      error
    );
    throw error;
  }
};

/**
 * Retrieves the list of SEF (System of Electronic Invoices) measurement units from the server.
 *
 * @async
 * @function retrieveSefMeasures
 * @returns {Promise<Array>} An array of SEF measurement unit objects.
 * @throws Will throw an error if the request to retrieve SEF measures fails.
 */
export const retrieveSefMeasures = async () => {
  try {
    const response = await axiosClient.get('/invoices/sef/measures');

    return response.data.items;
  } catch (error) {
    console.error('Failed to retrieve SEF measures:', error);
    throw error;
  }
};

/**
 * SEF's VAT categories and the exemption reasons each accepts, for issuing
 * invoices with VAT categories.
 *
 * @returns {Promise<{ categories: Array, exemptionReasons: Array }>}
 */
export const retrieveSefVatCatalog = async () => {
  const response = await axiosClient.get('/invoices/sef/vat-catalog');

  return response.data;
};

/**
 * The prepayment invoices a final invoice to one buyer may deduct, with what is
 * left of each per VAT category.
 *
 * @param {Object} params
 * @param {string} params.company
 * @param {string} params.customer - Customer id.
 * @param {string} [params.invoice] - The invoice being edited, whose own deductions do not count.
 * @returns {Promise<Array>}
 */
export const retrievePrepaymentInvoices = async ({ company, customer, invoice }) => {
  const response = await axiosClient.get('/invoices/prepayments', {
    params: { company, customer, ...(invoice ? { invoice } : {}) },
  });

  return response.data?.items || [];
};

/**
 * The NBS middle rate an invoice in another currency is converted at, for the
 * day its VAT obligation arises — the day the server picks.
 *
 * @param {{ currency, documentType, documentDate, supplyDate, invoicePeriodCode }} params - Dates as `YYYY-MM-DD`.
 * @returns {Promise<{ currency: string, date: string, rate: number|null }>}
 */
export const retrieveInvoiceExchangeRate = async (params) => {
  const response = await axiosClient.get('/invoices/exchange-rate', { params });

  return response.data;
};

/**
 * The invoices to one buyer that a credit or debit note may correct: sent to
 * SEF and not voided, newest first.
 *
 * @param {{ company: string, customer: string }} params
 * @returns {Promise<Array<{ _id, number, issueDate, total, documentType, status }>>}
 */
export const retrieveCorrectableInvoices = async ({ company, customer }) => {
  const response = await axiosClient.get('/invoices/correctable', { params: { company, customer } });

  return response.data?.items || [];
};

/**
 * Creates a new invoice by sending the provided payload to the server.
 *
 * @async
 * @function createInvoice
 * @param {Object} payload - The invoice data to be created (e.g. customer, items, amounts).
 * @returns {Promise<Object>} The newly created invoice data returned from the server.
 * @throws Will throw an error if the request to create the invoice fails.
 */
export const createInvoice = async (payload) => {
  try {
    const response = await axiosClient.post(`/invoices`, payload);

    return response.data;
  } catch (error) {
    console.error(`Failed to create Invoice`, error);
    throw error;
  }
};

/**
 * Retrieves an invoice by its ID.
 * @param {string} invoiceId - The unique identifier of the invoice.
 * @returns {Promise<Object>} A promise that resolves with the invoice data.
 * @throws Will throw an error if the request fails.
 */
export const getInvoiceById = async (invoiceId) => {
  try {
    const response = await axiosClient.get(`/invoices/${invoiceId}`);
    const { data } = response.data;

    return data;
  } catch (error) {
    console.error(`Failed to retrieve invoice with ID ${invoiceId}:`, error);
    throw error;
  }
};

/**
 * Updates an existing invoice by its ID.
 * Sends a PUT request to `/invoices/:invoiceId` with the provided payload.
 *
 * @param {string} invoiceId - The unique identifier of the invoice to update.
 * @param {Object} payload - The invoice fields to update (partial or full).
 * @returns {Promise<Object>} Resolves with the updated invoice data from the server.
 * @throws Will throw an error if the HTTP request fails.
 */
export const updateInvoiceById = async (invoiceId, payload) => {
  try {
    const response = await axiosClient.put(`/invoices/${invoiceId}`, payload);
    const { data } = response.data;

    return data;
  } catch (error) {
    console.error(`Failed to update invoice with ID ${invoiceId}:`, error);
    throw error;
  }
};

/**
 * Archives (soft-deletes) an invoice by its ID.
 * Sends a PATCH request to `/invoices/:invoiceId/archive`.
 *
 * @param {string} invoiceId - The unique identifier of the invoice to archive.
 * @returns {Promise<Object>} Resolves with the server response (typically the updated invoice or a status object).
 * @throws Will throw an error if the HTTP request fails.
 */
export const archiveInvoiceById = async (invoiceId) => {
  try {
    const response = await axiosClient.patch(`/invoices/${invoiceId}/archive`);

    return response.data;
  } catch (error) {
    console.error(`Failed to archive invoice with ID ${invoiceId}:`, error);
    throw error;
  }
};

/**
 * Restores (unarchives) a previously archived invoice by its ID.
 * Sends a PATCH request to `/invoices/:invoiceId/recover`.
 *
 * @param {string} invoiceId - The unique identifier of the invoice to restore.
 * @returns {Promise<Object>} Resolves with the server response (typically the updated invoice or a status object).
 * @throws Will throw an error if the HTTP request fails.
 */
export const unarchiveInvoiceById = async (invoiceId) => {
  try {
    const response = await axiosClient.patch(`/invoices/${invoiceId}/recover`);

    return response.data;
  } catch (error) {
    console.error(`Failed to unarchive invoice with ID ${invoiceId}:`, error);
    throw error;
  }
};

/**
 * Archives several invoices in one request.
 * Sends a PATCH request to `/invoices/bulk-archive`.
 *
 * @param {string[]} invoiceIds - Ids of the invoices to archive.
 * @returns {Promise<Object>} `{ archived, failed }` — the server archives what
 * it can and names what it could not, rather than failing the whole batch.
 * @throws Will throw an error if the HTTP request fails.
 */
export const archiveBulkInvoices = async (invoiceIds) => {
  try {
    const response = await axiosClient.patch('/invoices/bulk-archive', {
      invoiceIds,
    });

    return response.data;
  } catch (error) {
    console.error('Failed to bulk archive invoices:', error);
    throw error;
  }
};

/**
 * Marks incoming invoices as booked in the ERP, or clears the mark.
 * Sends a PATCH request to `/invoices/posted-to-erp`.
 *
 * One call for one row and for fifty — the screen ticks a batch, not a row at a
 * time.
 *
 * @param {string[]} invoiceIds - Ids of the invoices to mark.
 * @param {boolean} [posted=true] - `false` takes the mark back.
 * @returns {Promise<Object>} `{ updated, failed }` — marks what it can and
 * names what it could not, rather than failing the whole batch.
 * @throws Will throw an error if the HTTP request fails.
 */
export const setInvoicesPostedToErp = async (invoiceIds, posted = true) => {
  try {
    const response = await axiosClient.patch('/invoices/posted-to-erp', {
      invoiceIds,
      posted,
    });

    return response.data;
  } catch (error) {
    console.error('Failed to set the ERP posting mark:', error);
    throw error;
  }
};

/**
 * Marks invoices as already printed, or takes the mark back.
 *
 * `source` says how: `manual` for a tick by hand, `download` or `print` when
 * the screen reports a finished download or print. The server lets anything
 * but `manual` only fill an empty mark.
 *
 * @param {string[]} invoiceIds - Root invoice ids.
 * @param {boolean} [printed=true]
 * @param {string} [source='manual']
 * @returns {Promise<Object>} `{ data: { updated, failed } }`.
 */
export const setInvoicesPrinted = async (
  invoiceIds,
  printed = true,
  source = 'manual'
) => {
  try {
    const response = await axiosClient.patch('/invoices/printed', {
      invoiceIds,
      printed,
      source,
    });

    return response.data;
  } catch (error) {
    console.error('Failed to set the printed mark:', error);
    throw error;
  }
};

/**
 * Lists the people at a company who can be sent an approval reminder.
 * Sends a GET request to `/invoices/reminder-recipients`.
 *
 * @param {string} companyId - Whose people to list.
 * @returns {Promise<Array<Object>>} Users with `_id`, name, email and roles.
 * @throws Will throw an error if the HTTP request fails.
 */
export const retrieveReminderRecipients = async (companyId) => {
  try {
    const response = await axiosClient.get('/invoices/reminder-recipients', {
      params: { company: companyId },
    });

    return response.data?.items || [];
  } catch (error) {
    console.error('Failed to retrieve reminder recipients:', error);
    throw error;
  }
};

/**
 * Sends one approval reminder covering the selected invoices.
 * Sends a POST request to `/invoices/approval-reminder`.
 *
 * @param {Object} payload
 * @param {string} payload.companyId
 * @param {string[]} payload.invoiceIds
 * @param {string[]} payload.recipients - Email addresses.
 * @param {string} [payload.customText] - The sender's own note.
 * @returns {Promise<Object>} What was sent, to whom, and for which invoices.
 * @throws Will throw an error if the HTTP request fails.
 */
export const sendApprovalReminder = async (payload) => {
  try {
    const response = await axiosClient.post(
      '/invoices/approval-reminder',
      payload
    );

    return response.data;
  } catch (error) {
    console.error('Failed to send approval reminder:', error);
    throw error;
  }
};

/**
 * Deletes a invoice by their ID.
 * @param {string} invoiceId - The unique identifier of the invoice.
 * @returns {Promise<Object>} A promise that resolves when the invoice is deleted.
 * @throws Will throw an error if the delete request fails.
 */
export const deleteInvoiceById = async (invoiceId) => {
  try {
    const response = await axiosClient.delete(`/invoices/${invoiceId}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to delete invoice with ID ${invoiceId}:`, error);
    throw error;
  }
};

/**
 * Cancels (storno) a sales invoice by its ID.
 * Sends a PATCH request to `/invoices/:invoiceId/storno` with the storno comment.
 *
 * @param {string} invoiceId - The unique identifier of the invoice to cancel.
 * @param {Object} payload - The payload containing stornoComment.
 * @returns {Promise<Object>} Resolves with the server response confirming the storno.
 * @throws Will throw an error if the HTTP request fails.
 */
export const stornoInvoiceById = async (invoiceId, payload) => {
  try {
    const response = await axiosClient.patch(
      `/invoices/${invoiceId}/storno`,
      payload
    );

    return response.data;
  } catch (error) {
    console.error(`Failed to storno invoice with ID ${invoiceId}:`, error);
    throw error;
  }
};

/**
 * Synchronizes invoices from SEF for a specific company and date range.
 *
 * @async
 * @function syncInvoicesByDateRange
 * @param {Object} payload - The sync parameters.
 * @param {string} payload.companyId - The company ID to sync invoices for.
 * @param {string} payload.type - The invoice type ('ePurchaseInvoices' or 'eSalesInvoices').
 * @param {string} payload.dateFrom - Start date in YYYY-MM-DD format.
 * @param {string} payload.dateTo - End date in YYYY-MM-DD format.
 * @returns {Promise<Object>} The sync result from the server.
 * @throws Will throw an error if the sync request fails.
 */
/**
 * How long the browser waits for a sync before giving up.
 *
 * 🚨 Three minutes, against the client's default of one. A sync is not a query:
 * it fetches every document of the range from SEF, and those are measured in
 * megabytes — one supplier's are 16 to 22 MB each. A day of one company took
 * longer than the minute the client allowed, so the browser stopped listening
 * and showed "failed to sync" over a run that was working and went on to finish
 * — the request is not cancelled server-side, only abandoned here. Reported as
 * a failure, it sent people looking for a bug in the sync three times over.
 */
const SYNC_REQUEST_TIMEOUT_MS = 3 * 60 * 1000;

export const syncInvoicesByDateRange = async (payload) => {
  try {
    const response = await axiosClient.post('/invoices/sync', payload, {
      timeout: SYNC_REQUEST_TIMEOUT_MS,
    });
    return response.data;
  } catch (error) {
    console.error('Failed to sync invoices:', error);
    throw error;
  }
};

/**
 * Retrieves the projects a company offers on its invoice screens.
 *
 * Separate from the time-tracking project endpoints: those refuse any company
 * that has not switched that module on, and do not admit the eInvoiceOfficer
 * role at all.
 *
 * @async
 * @function retrieveInvoiceProjects
 * @param {string} company - Company id.
 * @param {Object} [options]
 * @param {boolean} [options.includeArchived=false] - Include archived projects,
 *   which cannot be selected but still label older invoices.
 * @returns {Promise<Object>} `{ count, items }`.
 * @throws Will throw an error if the HTTP request fails.
 */
export const retrieveInvoiceProjects = async (
  company,
  { includeArchived = false } = {}
) => {
  try {
    const response = await axiosClient.get('/invoices/projects', {
      params: { company, ...(includeArchived ? { includeArchived: true } : {}) },
    });

    return response.data;
  } catch (error) {
    console.error('Failed to retrieve invoice projects:', error);
    throw error;
  }
};

/**
 * Creates an invoice project.
 *
 * @async
 * @function createInvoiceProject
 * @param {Object} payload - `{ company, name, code, description, approvers }`.
 * @returns {Promise<Object>} The created project.
 * @throws Will throw an error if the HTTP request fails.
 */
export const createInvoiceProject = async (payload) => {
  try {
    const response = await axiosClient.post('/invoices/projects', payload);
    return response.data;
  } catch (error) {
    console.error('Failed to create invoice project:', error);
    throw error;
  }
};

/**
 * Updates an invoice project, including who may confirm against it.
 *
 * @async
 * @function updateInvoiceProject
 * @param {string} projectId - Project id.
 * @param {Object} payload - `{ company, ...fields }`.
 * @returns {Promise<Object>} The updated project.
 * @throws Will throw an error if the HTTP request fails.
 */
export const updateInvoiceProject = async (projectId, payload) => {
  try {
    const response = await axiosClient.put(
      `/invoices/projects/${projectId}`,
      payload
    );

    return response.data;
  } catch (error) {
    console.error(`Failed to update invoice project ${projectId}:`, error);
    throw error;
  }
};

/**
 * Moves an already-approved invoice to a different project.
 *
 * SEF is not contacted and the invoice keeps its status; the reason is required
 * and is stored with the audit entry beside the old and new value.
 *
 * @async
 * @function reassignInvoiceProject
 * @param {string} invoiceId - Root invoice id.
 * @param {Object} payload - `{ project, transactionDescription, reason }`.
 * @returns {Promise<Object>} `{ from, to, reason }`.
 * @throws Will throw an error if the HTTP request fails.
 */
export const reassignInvoiceProject = async (invoiceId, payload) => {
  try {
    const response = await axiosClient.patch(
      `/invoices/${invoiceId}/project`,
      payload
    );

    return response.data;
  } catch (error) {
    console.error(`Failed to reassign project on invoice ${invoiceId}:`, error);
    throw error;
  }
};

/**
 * The invoice PDF with the approval strip under every page.
 *
 * Fetched through the API rather than linked from storage: the strip is applied
 * to the bytes on their way out, and the stored file is deliberately left as
 * the tax office issued it.
 *
 * @param {string} invoiceId - Root invoice id.
 * @returns {Promise<Blob>}
 */
export const retrieveStampedInvoicePdf = async (invoiceId) => {
  try {
    const response = await axiosClient.get(`/invoices/${invoiceId}/pdf`, {
      responseType: 'blob',
    });

    return response.data;
  } catch (error) {
    console.error('Failed to retrieve the stamped invoice PDF:', error);
    throw error;
  }
};

/**
 * The lines of one invoice with what the codebook proposes for each.
 *
 * Read on demand rather than with the table: lines are kept out of the list
 * payload on purpose, and the proposal is computed fresh so it reflects the
 * codebook as it stands now.
 *
 * @param {string} invoiceId - Root invoice id.
 * @returns {Promise<Object>} `{ locked, lines[], vatAmount, ... }`.
 */
export const retrieveVatLines = async (invoiceId) => {
  try {
    const response = await axiosClient.get(`/invoices/${invoiceId}/vat-lines`);

    return response.data?.data ?? response.data;
  } catch (error) {
    console.error('Failed to retrieve VAT lines:', error);
    throw error;
  }
};

/**
 * Files which lines are deductible, and teaches the codebook from them.
 *
 * @param {string} invoiceId - Root invoice id.
 * @param {Object} payload - `{ lines: [{ ordinal, deductible }] }`.
 * @returns {Promise<Object>} `{ value, deductibleVat, nonDeductibleVat, learned }`.
 */
export const confirmVatLines = async (invoiceId, payload) => {
  try {
    const response = await axiosClient.patch(
      `/invoices/${invoiceId}/vat-lines`,
      payload
    );

    return response.data;
  } catch (error) {
    console.error('Failed to confirm VAT lines:', error);
    throw error;
  }
};

/**
 * Records a person's decision overriding the calculated VAT deductibility.
 *
 * The reason is required and the server refuses without it: the calculation
 * follows from the document, so contradicting it without saying why cannot be
 * reviewed later — and this table is handed to an accounting agency.
 *
 * @async
 * @function overrideVatDeductible
 * @param {string} invoiceId - Root invoice id.
 * @param {Object} payload - `{ vatDeductible, reason }`.
 * @returns {Promise<Object>} `{ from, to, reason }`.
 * @throws Will throw an error if the HTTP request fails.
 */
export const overrideVatDeductible = async (invoiceId, payload) => {
  try {
    const response = await axiosClient.patch(
      `/invoices/${invoiceId}/vat-deductible`,
      payload
    );

    return response.data;
  } catch (error) {
    console.error(
      `Failed to override VAT deductibility on invoice ${invoiceId}:`,
      error
    );
    throw error;
  }
};

/**
 * The book counted six ways, for whatever the screen is currently showing.
 *
 * One request rather than six: every panel in the statistics view has to answer
 * for the same set of rows as the table behind it.
 *
 * @async
 * @function retrieveJournalStats
 * @param {string} companyId
 * @param {Object} filters - The same normalised filter the list is built from.
 * @returns {Promise<Object>}
 * @throws Will throw an error if the HTTP request fails.
 */
export const retrieveJournalStats = async (companyId, filters = {}) => {
  try {
    const response = await axiosClient.get(`/invoices/journal-stats/${companyId}`, {
      params: { filter: JSON.stringify(filters) },
    });

    return response.data?.data;
  } catch (error) {
    console.error(`Failed to load the book statistics for ${companyId}:`, error);
    throw error;
  }
};

/**
 * Writes the company's own nine book columns on one invoice.
 *
 * A subset is a valid payload — the agency answers two of the cells and the
 * project team three, and nobody fills a row in one sitting. Anything left out
 * stays as it was.
 *
 * @async
 * @function updateJournalNotes
 * @param {string} invoiceId - Root invoice id.
 * @param {Object} payload - Any subset of the nine keys.
 * @returns {Promise<Object>} The nine cells as they now stand.
 * @throws Will throw an error if the HTTP request fails.
 */
export const updateJournalNotes = async (invoiceId, payload) => {
  try {
    const response = await axiosClient.patch(
      `/invoices/${invoiceId}/journal-notes`,
      payload
    );

    return response.data;
  } catch (error) {
    console.error(
      `Failed to update the book columns on invoice ${invoiceId}:`,
      error
    );
    throw error;
  }
};

/**
 * Totals for the book over the whole filtered set.
 *
 * Deliberately a request rather than a sum of the rows on screen: the table is
 * paginated, so a client-side total would change with the page size — a bad way
 * to be wrong about a figure that goes on a handover document.
 *
 * @async
 * @function retrieveJournalTotals
 * @param {string} companyId
 * @param {Object} filters - The same filter object the list is sent.
 * @returns {Promise<Object>} Counts and per-currency totals.
 */
export const retrieveJournalTotals = async (companyId, filters) => {
  try {
    const response = await axiosClient.get(
      `/invoices/journal-totals/${companyId}`,
      {
        params: { filter: filters },
        paramsSerializer: (p) => {
          if (p.filter) p.filter = JSON.stringify(p.filter);

          return qs.stringify(p, { encode: true });
        },
      }
    );

    return response.data?.data || null;
  } catch (error) {
    console.error('Failed to retrieve journal totals:', error);
    throw error;
  }
};


/**
 * One month of the book, broken down by project, with each handover's state.
 *
 * @async
 * @function retrieveJournalMonth
 * @param {string} companyId
 * @param {number} year
 * @param {number} month
 * @returns {Promise<Object>}
 */
export const retrieveJournalMonth = async (companyId, year, month) => {
  try {
    const response = await axiosClient.get(
      `/invoices/journal-month/${companyId}`,
      { params: { year, month } }
    );

    return response.data?.data || null;
  } catch (error) {
    console.error('Failed to retrieve journal month:', error);
    throw error;
  }
};

/**
 * Closes a month of one project and records it as handed over.
 *
 * @async
 * @function submitJournalPeriod
 * @param {string} companyId
 * @param {Object} payload - `{ project, year, month, note }`.
 * @returns {Promise<Object>}
 */
export const submitJournalPeriod = async (companyId, payload) => {
  try {
    const response = await axiosClient.post(
      `/invoices/journal-periods/${companyId}/submit`,
      payload
    );

    return response.data;
  } catch (error) {
    console.error('Failed to submit journal period:', error);
    throw error;
  }
};

/**
 * Records the accounting agency's confirmation of receipt.
 *
 * @async
 * @function confirmJournalPeriodReceipt
 * @param {string} companyId
 * @param {string} periodId
 * @param {Object} payload - `{ receivedByName, receivedAt }`.
 * @returns {Promise<Object>}
 */
export const confirmJournalPeriodReceipt = async (
  companyId,
  periodId,
  payload
) => {
  try {
    const response = await axiosClient.patch(
      `/invoices/journal-periods/${companyId}/${periodId}/receipt`,
      payload
    );

    return response.data;
  } catch (error) {
    console.error('Failed to confirm journal receipt:', error);
    throw error;
  }
};

/**
 * Reopens a closed month. The reason is required.
 *
 * @async
 * @function reopenJournalPeriod
 * @param {string} companyId
 * @param {string} periodId
 * @param {Object} payload - `{ reason }`.
 * @returns {Promise<Object>}
 */
export const reopenJournalPeriod = async (companyId, periodId, payload) => {
  try {
    const response = await axiosClient.patch(
      `/invoices/journal-periods/${companyId}/${periodId}/reopen`,
      payload
    );

    return response.data;
  } catch (error) {
    console.error('Failed to reopen journal period:', error);
    throw error;
  }
};

/**
 * The suppliers whose input VAT a company may not deduct (Article 29).
 *
 * @async
 * @function retrieveVatExclusions
 * @param {string} companyId
 * @param {boolean} [includeInactive]
 * @returns {Promise<Array>}
 */
export const retrieveVatExclusions = async (
  companyId,
  includeInactive = false
) => {
  try {
    const response = await axiosClient.get(
      `/invoices/vat-exclusions/${companyId}`,
      { params: includeInactive ? { includeInactive: 'true' } : {} }
    );

    return response.data?.data || [];
  } catch (error) {
    console.error('Failed to retrieve VAT exclusions:', error);
    throw error;
  }
};

/**
 * Adds a supplier to the Article 29 list. The server recalculates the
 * company's invoices at once, so the effect is visible immediately.
 *
 * @async
 * @function createVatExclusion
 * @param {string} companyId
 * @param {Object} payload - `{ supplierVat, supplierName, reason }`.
 * @returns {Promise<Object>}
 */
export const createVatExclusion = async (companyId, payload) => {
  try {
    const response = await axiosClient.post(
      `/invoices/vat-exclusions/${companyId}`,
      payload
    );

    return response.data;
  } catch (error) {
    console.error('Failed to create VAT exclusion:', error);
    throw error;
  }
};

/**
 * Edits or switches off an Article 29 entry.
 *
 * @async
 * @function updateVatExclusion
 * @param {string} companyId
 * @param {string} exclusionId
 * @param {Object} payload
 * @returns {Promise<Object>}
 */
export const updateVatExclusion = async (companyId, exclusionId, payload) => {
  try {
    const response = await axiosClient.put(
      `/invoices/vat-exclusions/${companyId}/${exclusionId}`,
      payload
    );

    return response.data;
  } catch (error) {
    console.error('Failed to update VAT exclusion:', error);
    throw error;
  }
};

/**
 * Removes an Article 29 entry.
 *
 * @async
 * @function deleteVatExclusion
 * @param {string} companyId
 * @param {string} exclusionId
 * @returns {Promise<Object>}
 */
export const deleteVatExclusion = async (companyId, exclusionId) => {
  try {
    const response = await axiosClient.delete(
      `/invoices/vat-exclusions/${companyId}/${exclusionId}`
    );

    return response.data;
  } catch (error) {
    console.error('Failed to delete VAT exclusion:', error);
    throw error;
  }
};

/**
 * Records an incoming purchase document that did not come from SEF.
 *
 * Fiscal receipts, other machine-issued invoices, import documentation and petty
 * cash — about 7% of what a company buys. Stored as the same kind of document as
 * an electronic invoice, carrying `origin: manual`, so the list, the project and
 * the export need no separate handling.
 *
 * @async
 * @function createManualPurchaseInvoice
 * @param {Object} payload - `{ company, documentNumber, documentDate,
 *   supplierName, manualDocumentType, total, ... }`.
 * @returns {Promise<Object>} `{ invoiceId, referenceId, status }`.
 * @throws Will throw an error if the HTTP request fails.
 */
export const createManualPurchaseInvoice = async (payload) => {
  try {
    const response = await axiosClient.post('/invoices/manual', payload);
    return response.data;
  } catch (error) {
    console.error('Failed to create manual purchase invoice:', error);
    throw error;
  }
};

/**
 * Corrects a manual document that has not been confirmed yet.
 *
 * @async
 * @function updateManualPurchaseInvoice
 * @param {string} invoiceId - Root invoice id.
 * @param {Object} payload - Any subset of the fields.
 * @returns {Promise<Object>} The updated document.
 * @throws Will throw an error if the HTTP request fails.
 */
export const updateManualPurchaseInvoice = async (invoiceId, payload) => {
  try {
    const response = await axiosClient.put(
      `/invoices/manual/${invoiceId}`,
      payload
    );

    return response.data;
  } catch (error) {
    console.error(`Failed to update manual invoice ${invoiceId}:`, error);
    throw error;
  }
};

/**
 * Confirms a manual document. SEF is never contacted — there is nothing there to
 * accept. When the company requires projects, both the project and the
 * transaction description are mandatory.
 *
 * @async
 * @function confirmManualPurchaseInvoice
 * @param {string} invoiceId - Root invoice id.
 * @param {Object} payload - `{ project, transactionDescription }`.
 * @returns {Promise<Object>} `{ invoiceId, updatedStatus }`.
 * @throws Will throw an error if the HTTP request fails.
 */
export const confirmManualPurchaseInvoice = async (invoiceId, payload) => {
  try {
    const response = await axiosClient.patch(
      `/invoices/manual/${invoiceId}/confirm`,
      payload
    );

    return response.data;
  } catch (error) {
    console.error(`Failed to confirm manual invoice ${invoiceId}:`, error);
    throw error;
  }
};

/**
 * Removes a manual document that has not been confirmed yet.
 *
 * @async
 * @function deleteManualPurchaseInvoice
 * @param {string} invoiceId - Root invoice id.
 * @returns {Promise<Object>} `{ invoiceId }`.
 * @throws Will throw an error if the HTTP request fails.
 */
export const deleteManualPurchaseInvoice = async (invoiceId) => {
  try {
    const response = await axiosClient.delete(`/invoices/manual/${invoiceId}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to delete manual invoice ${invoiceId}:`, error);
    throw error;
  }
};

/**
 * The audit trail of one incoming invoice: who changed what, and when.
 *
 * 🚨 `audit-logs` and not `timeline`. The timeline route derives its history
 * model name as `<model>History`, which for this model would be
 * `ElectronicPurchaseInvoiceHistory` — the registered name is
 * `ElectronicPurchaseHistory`, so that route throws for invoices. This one reads
 * the audit collection by the `model` field and works.
 *
 * @param {string} invoiceId - The ElectronicPurchaseInvoice id (the reference,
 *   not the root Invoice — the audit rows are written against the reference).
 */
export const retrieveInvoiceAuditLog = async (invoiceId) => {
  try {
    const response = await axiosClient.get(
      `/history/ElectronicPurchaseInvoice/${invoiceId}/audit-logs`
    );

    return response.data?.data || [];
  } catch (error) {
    console.error('Failed to get invoice audit log:', error);

    return [];
  }
};

const InvoicesService = {
  retrieveAllInvoices,
  updateInvoiceStatusById,
  retrieveSefMeasures,
  retrieveSefVatCatalog,
  retrievePrepaymentInvoices,
  retrieveCorrectableInvoices,
  retrieveInvoiceExchangeRate,
  createInvoice,
  getInvoiceById,
  updateInvoiceById,
  archiveInvoiceById,
  unarchiveInvoiceById,
  archiveBulkInvoices,
  setInvoicesPostedToErp,
  setInvoicesPrinted,
  retrieveReminderRecipients,
  sendApprovalReminder,
  deleteInvoiceById,
  stornoInvoiceById,
  syncInvoicesByDateRange,
  retrieveInvoiceProjects,
  createInvoiceProject,
  updateInvoiceProject,
  reassignInvoiceProject,
  overrideVatDeductible,
  updateJournalNotes,
  retrieveJournalTotals,
  retrieveJournalStats,
  retrieveJournalMonth,
  submitJournalPeriod,
  confirmJournalPeriodReceipt,
  reopenJournalPeriod,
  retrieveVatExclusions,
  createVatExclusion,
  updateVatExclusion,
  deleteVatExclusion,
  createManualPurchaseInvoice,
  updateManualPurchaseInvoice,
  confirmManualPurchaseInvoice,
  deleteManualPurchaseInvoice,
  retrieveInvoiceAuditLog,
};

export default InvoicesService;

// ── Messages on an invoice ──────────────────────────────────────────────────

/** The conversation on one invoice, oldest first. Opening it marks it read. */
export const retrieveInvoiceMessages = async (invoiceId) => {
  const response = await axiosClient.get(`/invoices/${invoiceId}/messages`);

  return response.data?.data || [];
};

/** Who a message on this invoice can go to, and who it most likely should. */
export const retrieveInvoiceMessageRecipients = async (invoiceId) => {
  const response = await axiosClient.get(`/invoices/${invoiceId}/messages/recipients`);

  return response.data?.data || { items: [], suggested: [] };
};

export const postInvoiceMessage = async (invoiceId, { text, recipients }) => {
  const response = await axiosClient.post(`/invoices/${invoiceId}/messages`, {
    text,
    recipients,
  });

  return response.data?.data;
};

/** `instant` — a mail per message; `daily` — one summary in the morning. */
export const retrieveInvoiceMessageSetting = async () => {
  const response = await axiosClient.get('/invoices/messages/settings');

  return response.data?.data || { emailMode: 'instant' };
};

export const saveInvoiceMessageSetting = async (emailMode) => {
  const response = await axiosClient.put('/invoices/messages/settings', { emailMode });

  return response.data?.data;
};

/** How many of a company's invoices have a message waiting for the caller. */
export const retrieveUnreadInvoiceMessageCount = async (companyId) => {
  const response = await axiosClient.get('/invoices/messages/unread-count', {
    params: { company: companyId },
  });

  return response.data?.data?.count || 0;
};

/** Everything waiting for the caller across every company, newest first. */
export const retrieveInvoiceMessageInbox = async () => {
  const response = await axiosClient.get('/invoices/messages/inbox');

  return response.data?.data || { count: 0, items: [] };
};

/**
 * Brings the company's notices on input VAT over from SEF — both those it
 * filed (here or on SEF by hand) and those its customers filed to it.
 *
 * @param {string} companyId
 * @returns {Promise<{ sent: number, received: number }>}
 */
export const syncVatNotices = async (companyId) => {
  try {
    const response = await axiosClient.post(
      `/invoices/vat-notices/${companyId}/sync`
    );

    return response.data?.data;
  } catch (error) {
    console.error('Failed to sync VAT notices:', error);
    throw error;
  }
};

/**
 * Storno and credit notes still waiting for a notice, each prefilled.
 *
 * @param {string} companyId
 * @param {string} [from] - `YYYY-MM-DD`; the server defaults to last month.
 * @returns {Promise<{ from: string, lastNoticeNumber: string, candidates: Object[] }>}
 */
export const retrieveVatNoticeCandidates = async (companyId, from) => {
  try {
    const response = await axiosClient.get(
      `/invoices/vat-notices/${companyId}/candidates`,
      { params: from ? { from } : {} }
    );

    return response.data?.data;
  } catch (error) {
    console.error('Failed to retrieve VAT notice candidates:', error);
    throw error;
  }
};

/**
 * Notices the company holds.
 *
 * @param {string} companyId
 * @param {'sent'|'received'|'dismissed'} kind
 * @returns {Promise<Object[]>}
 */
export const retrieveVatNotices = async (companyId, kind = 'sent') => {
  try {
    const response = await axiosClient.get(
      `/invoices/vat-notices/${companyId}`,
      { params: { kind } }
    );

    return response.data?.data || [];
  } catch (error) {
    console.error('Failed to retrieve VAT notices:', error);
    throw error;
  }
};

/**
 * Files the chosen notices at SEF. Final — SEF cannot withdraw one.
 *
 * @param {string} companyId
 * @param {Array<{ purchaseInvoiceId: string, noticeNumber: string, notes: string, documentReferenceType: string }>} items
 * @returns {Promise<Array<{ purchaseInvoiceId: string, ok: boolean, error?: string }>>}
 */
export const sendVatNotices = async (companyId, items) => {
  try {
    const response = await axiosClient.post(
      `/invoices/vat-notices/${companyId}/send`,
      { items }
    );

    return response.data?.data || [];
  } catch (error) {
    console.error('Failed to send VAT notices:', error);
    throw error;
  }
};

/**
 * Marks documents as needing no notice, or takes that back.
 *
 * @param {string} companyId
 * @param {string[]} purchaseInvoiceIds
 * @param {boolean} [dismissed=true]
 */
export const dismissVatNotices = async (
  companyId,
  purchaseInvoiceIds,
  dismissed = true
) => {
  try {
    const response = await axiosClient.post(
      `/invoices/vat-notices/${companyId}/dismiss`,
      { purchaseInvoiceIds, dismissed }
    );

    return response.data?.data;
  } catch (error) {
    console.error('Failed to dismiss VAT notices:', error);
    throw error;
  }
};

/**
 * SEF's PDF of one notice.
 *
 * @param {string} companyId
 * @param {string} noticeId - Our row id.
 * @returns {Promise<Blob>}
 */
export const retrieveVatNoticePdf = async (companyId, noticeId) => {
  try {
    const response = await axiosClient.get(
      `/invoices/vat-notices/${companyId}/${noticeId}/pdf`,
      { responseType: 'blob' }
    );

    return response.data;
  } catch (error) {
    console.error('Failed to retrieve the VAT notice PDF:', error);
    throw error;
  }
};

// ── Payments read off the bank statements ───────────────────────────────────

/** Statement lines whose invoice is only a guess. `direction`: out | in. */
export const retrievePaymentSuggestions = async (companyId, direction = 'out') => {
  const response = await axiosClient.get(`/invoices/payments/${companyId}/suggestions`, {
    params: { direction },
  });

  return response.data?.data || [];
};

/** How many statement lines wait for a decision, per direction: `{ out, in }`. */
export const retrievePaymentSuggestionCount = async (companyId) => {
  const response = await axiosClient.get(`/invoices/payments/${companyId}/suggestions/count`);

  return response.data?.data || {};
};

/** Confirms or turns down pairings. `decision`: confirm | reject. */
export const decidePaymentMatches = async (companyId, matchIds, decision) => {
  const response = await axiosClient.post(`/invoices/payments/${companyId}/decide`, {
    matchIds,
    decision,
  });

  return response.data?.data;
};

/**
 * What is to be paid now on a supplier invoice, when less than its total —
 * `amount: null` gives the invoice back to its note.
 */
export const setInvoiceAmountToPay = async (companyId, invoiceId, amount) => {
  const response = await axiosClient.put(
    `/invoices/payments/${companyId}/invoice/${invoiceId}/amount-to-pay`,
    { amount }
  );

  return response.data?.data;
};

/** The payments of one invoice — `invoiceId` is the reference document's id. */
export const retrieveInvoicePayments = async (companyId, invoiceId) => {
  const response = await axiosClient.get(
    `/invoices/payments/${companyId}/invoice/${invoiceId}`
  );

  return response.data?.data || [];
};
